import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight } from '@gravity-ui/icons';
import ScopeFilter from '../../../components/ScopeFilter/ScopeFilter';
import { DateTimeInput } from '../../../components/DateTimeInput';
import ViolationStatusBadge from './ViolationStatusBadge';
import ViolationStateBlock from './ViolationStateBlock';
import ViolationEvidenceThumbnail from './ViolationEvidenceThumbnail';
import EvidenceLightbox from './EvidenceLightbox';
import EventBulkActionsBar from './EventBulkActionsBar';
import EventsSelectionStats from './EventsSelectionStats';
import DuplicatesPanel from './DuplicatesPanel';
import useEventSelection, { selectionStorageKey } from './useEventSelection';
import NumberedPagination from '../../../components/NumberedPagination/NumberedPagination';
import { ResizableColgroup, ResizableTh } from '../../../components/ColumnResizeHandle/ColumnResizeHandle';
import { useResizableColumns } from '../../../hooks/useResizableColumns';
import {
  bulkSetEventAnalyticsExclusion,
  listUnifiedEvents,
  listViolationEvents,
} from '../../../services/violations';
import { listBusinessEvents } from '../../../services/businessAnalytics';
import { useNavigationEntitlements } from '../../../hooks/useNavigationEntitlements';
import {
  EVENT_TYPE_LABELS,
  EVENT_TYPE_OPTIONS,
  allowedEventTypes,
  eventTypeOf,
  eventsSourceFor,
  parseEventTypes,
  serializeEventTypes,
} from './eventTypesModel';
import {
  ANALYTICS_GROUP,
  DUPLICATE_GROUP,
  STATUS_GROUP,
  buildEventsQuery,
  createEmptyEventFilters,
  eventFiltersKey,
  groupChecks,
  groupValue,
  isEmptyEventFilters,
  parseEventFilters,
  toCategoryParams,
  writeEventFilters,
} from './eventsQueryModel';
import { formatEventCondition } from '../../../utils/ruleFormat';
import { useRuleCatalogs } from '../../../hooks/useRuleCatalog';
import { useListScrollRestore } from '../../../hooks/useListScrollRestore';
import { useAutoDismiss } from '../../../hooks/useAutoDismiss';
import { isTimezoneAwareIso, toLocalInputValue } from '../../../utils/toIsoWithOffset';
import { describeUserError, describeWriteError } from '../../../utils/apiError';
import { formatInTimezone } from '../../../utils/formatInTimezone';
import { useUrlState } from '../../../hooks/useUrlState';
import { useCameraNames } from '../../../hooks/useCameraNames';
import TimezoneNotice from '../../SummaryPage/components/TimezoneNotice';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  getVerdictLabel,
} from '../../../constants/violationConstants';

const FILTER_SELECTION_HINT = 'Выбраны все события по фильтру. Снимите выбор, чтобы отмечать события по одному.';

// FE-FILTER02: страница и размер страницы — в URL через useUrlState. Фильтр
// пишет в URL eventsQueryModel: несколько значений на уровень в схему
// useUrlState не укладываются. Схема — стабильная ссылка на уровне модуля.
const PAGE_URL_SCHEMA = {
  limit: { param: 'limit', default: DEFAULT_PAGE_SIZE, type: 'number' },
  offset: { param: 'offset', default: 0, type: 'number' },
};

/*
 * Список по фильтру. Один тип событий и не больше одного значения в каждом
 * поле — категорийный список, как раньше; иначе unified, который принимает
 * массивы. Этим же путём идут «Посчитать» (отмеченные id) и «Дубли».
 */
const listEventsFor = ({ limit, offset, ...query }) => {
  const types = query.event_types || [];
  if (types.length === 0) return Promise.resolve({ items: [], total: 0 });
  if (types.length === 1) {
    const single = toCategoryParams(query);
    if (single) {
      const list = types[0] === 'business' ? listBusinessEvents : listViolationEvents;
      return list({ ...single, limit, offset });
    }
  }
  return listUnifiedEvents({ ...query, limit, offset });
};

// Черновик держит отмеченные галочки как есть: «обе» и «ни одной» значат одно
// и то же, но человек видит то, что отметил.
const toDraft = (filters) => ({
  scope: filters.scope,
  types: filters.types,
  status: groupChecks(filters.status),
  analytics: groupChecks(filters.analytics),
  duplicate: groupChecks(filters.duplicate),
  from: filters.from,
  to: filters.to,
});

const fromDraft = (draft) => ({
  scope: draft.scope,
  types: draft.types,
  status: groupValue(draft.status, STATUS_GROUP),
  analytics: groupValue(draft.analytics, ANALYTICS_GROUP),
  duplicate: groupValue(draft.duplicate, DUPLICATE_GROUP),
  from: draft.from,
  to: draft.to,
});

const toggleValue = (values, value) => (
  values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
);

/** Группа галочек фильтра. Ни одной или все — «все»; `noneIsEmpty` — ни одной значит «ничего». */
const CheckGroup = ({ legend, options, checked, onToggle, noneIsEmpty = false }) => {
  const all = checked.length === options.length || (!noneIsEmpty && checked.length === 0);
  let counter = `выбрано ${checked.length}`;
  if (all) counter = 'все';
  else if (checked.length === 0) counter = 'ничего';
  return (
    <fieldset className="vio-check-group">
      <legend className="vio-check-group-head">
        <span className="vio-field-label">{legend}</span>
        <span className={all ? 'scope-level-count' : 'scope-level-count scope-level-count--active'}>
          {counter}
        </span>
      </legend>
      {options.map((option) => (
        <label key={option.value} className="vio-type-option">
          <input
            type="checkbox"
            checked={checked.includes(option.value)}
            onChange={() => onToggle(option.value)}
          />
          {option.label}
        </label>
      ))}
    </fieldset>
  );
};

/*
 * P2-07 / Extended event filters and fields.
 * Дополнительные фильтры и колонки списка событий появятся только после того,
 * как backend заморозит расширенный DTO. Ничего не додумываем здесь заранее.
 */

const ViolationEventsTab = () => {
  const navigate = useNavigate();

  // FE-FILTER02: применённый фильтр, страница и размер страницы живут в URL —
  // Back из карточки события и reload должны вернуть тот же список.
  const [searchParams, setSearchParams] = useSearchParams();
  const [url, setUrl] = useUrlState(PAGE_URL_SCHEMA);
  const { limit, offset } = url;
  const appliedKey = eventFiltersKey(parseEventFilters(searchParams));
  const appliedFilters = useMemo(() => JSON.parse(appliedKey), [appliedKey]);
  const [draft, setDraft] = useState(() => toDraft(appliedFilters));
  // Back/Forward меняет URL напрямую, минуя черновик.
  useEffect(() => { setDraft(toDraft(appliedFilters)); }, [appliedFilters]);
  const draftFilters = useMemo(() => fromDraft(draft), [draft]);
  const dirty = eventFiltersKey(draftFilters) !== appliedKey;

  // Возврат из события, открытого вне списка (диаграмма Ганта): в state
  // приходит событие, список сам находит его страницу и строку. Порядок —
  // started_at по убыванию, поэтому номер строки = число событий не раньше
  // него; from у backend включительный.
  const location = useLocation();
  const focusRef = useRef(location.state?.focusEvent || null);
  const [focusPending, setFocusPending] = useState(Boolean(focusRef.current?.startedAt));
  // Страница события, пока URL ещё не догнал её offset.
  const [focusOffset, setFocusOffset] = useState(null);
  const focusWaiting = focusPending || (focusOffset !== null && focusOffset !== offset);

  // ТЗ V5 №8: доступные типы — из granted_keys №7, выбранные — из URL.
  const { keys: navigationKeys } = useNavigationEntitlements();
  const allowedTypes = useMemo(() => allowedEventTypes(navigationKeys), [navigationKeys]);
  const appliedTypes = useMemo(
    () => parseEventTypes(appliedFilters.types, allowedTypes),
    [appliedFilters.types, allowedTypes]
  );
  const draftTypes = parseEventTypes(draft.types, allowedTypes);
  const source = eventsSourceFor(appliedTypes);
  const violationOnly = source === 'violation';

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  // ТЗ №2 §10.3: timezone_mode/display_timezone(s) из list response — подпись над таблицей.
  const [tzScope, setTzScope] = useState(null);
  const [listState, setListState] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');

  // №20: выбор копится между страницами и живёт в sessionStorage (переживает Detail/Back).
  const selection = useEventSelection({ storageKey: selectionStorageKey() });
  const resize = useResizableColumns('violations_events_widths');
  const { syncFilters } = selection;

  // FE-FIX15: массовое исключение событий из аналитики.
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNotice, setBulkNotice] = useState('');
  const [bulkError, setBulkError] = useState('');

  // FE-FIX08: результат массового действия гаснет сам, ошибка остаётся.
  useAutoDismiss(bulkNotice, setBulkNotice);

  const [lightbox, setLightbox] = useState(null);

  // Применённый фильтр в форме unified, без пагинации — общий для списка,
  // выбора «все по фильтру», «Посчитать» и «Дублей».
  const query = useMemo(
    () => buildEventsQuery(appliedFilters, appliedTypes),
    [appliedFilters, appliedTypes]
  );

  useEffect(() => {
    if (!focusPending) return undefined;
    let cancelled = false;
    const done = () => { if (!cancelled) setFocusPending(false); };
    if (source === 'none') { done(); return undefined; }
    listEventsFor({ ...query, from: focusRef.current.startedAt, limit: 1, offset: 0 })
      .then((response) => {
        if (cancelled) return;
        const position = Math.max(0, (Number(response.total) || 1) - 1);
        const target = Math.floor(position / limit) * limit;
        if (target !== offset) {
          setFocusOffset(target);
          setUrl({ offset: target });
        }
      })
      .catch(() => {})
      .finally(done);
    return () => { cancelled = true; };
    // Однократно при входе: дальнейшие смены фильтра — обычная работа списка.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusPending]);

  // Ответ устаревшего набора фильтров не должен перезаписать новый.
  const requestRef = useRef(0);
  const loadEvents = useCallback(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    if (source === 'none') {
      // Ни одного типа — явно пустая выборка, запросов нет.
      setItems([]);
      setTotal(0);
      setTzScope(null);
      setListState('ready');
      return Promise.resolve();
    }
    setListState('loading');
    return listEventsFor({ ...query, limit, offset })
      .then((response) => {
        if (requestRef.current !== requestId) return;
        setItems(response.items);
        setTotal(response.total);
        setTzScope(response.timezone_mode ? {
          timezone_mode: response.timezone_mode,
          display_timezone: response.display_timezone,
          display_timezones: response.display_timezones,
        } : null);
        setListState('ready');
      })
      .catch((error) => {
        if (requestRef.current !== requestId) return;
        setItems([]);
        setTotal(0);
        setErrorMessage(describeUserError(error, violationOnly
          ? 'Не удалось загрузить список нарушений'
          : 'Не удалось загрузить список событий'));
        setListState('error');
      });
  }, [source, violationOnly, query, limit, offset]);

  useEffect(() => {
    // Пока ищем страницу события из state, нулевую страницу не грузим зря.
    if (focusWaiting) return;
    setFocusOffset(null);
    loadEvents();
  }, [loadEvents, focusWaiting]);

  // Выбор «по фильтру» привязан к фильтру: сменили фильтр — режим выключается.
  // Выбор отдельных событий (режим ids) при смене фильтра сохраняется.
  useEffect(() => { syncFilters(query); }, [query, syncFilters]);

  // Finding 5: имя и таймзона камеры не приходят в событии — общий кэш
  // useCameraNames вместо copy-paste Map+cancelled-эффекта.
  const { cameraLabel, cameraTimezone } = useCameraNames(items);

  // Камеры фильтра: имя для камеры, пришедшей ссылкой из «Сводки» без
  // родительской цепочки, и часовой пояс периода, если камера одна (ТЗ №2
  // §10.4: при camera_id backend трактует naive from/to как local wall time
  // камеры; мы шлём tz-aware, но пояс фильтра всё равно показываем).
  const draftCameraIds = draft.scope.cameraIds;
  const filterCameraItems = useMemo(
    () => draftCameraIds.map((id) => ({ camera_id: Number(id) })),
    [draftCameraIds]
  );
  const { cameraLabel: filterCameraLabel, cameraTimezone: filterCameraTimezone } = useCameraNames(filterCameraItems);
  const singleCameraTimezone = draftCameraIds.length === 1 ? filterCameraTimezone(Number(draftCameraIds[0])) : '';

  // Названия правил, которые встретились в списке: строки кандидатов дублей
  // и правило из ссылки, которого нет в опциях фильтра.
  const ruleNames = useMemo(() => {
    const seen = {};
    items.forEach((event) => {
      if (event.rule_id && !seen[event.rule_id]) seen[event.rule_id] = event.rule_name || event.rule_id;
    });
    return seen;
  }, [items]);

  const knownNames = useMemo(() => ({
    cameraIds: Object.fromEntries(draftCameraIds.map((id) => [id, filterCameraLabel(Number(id))])),
    ruleIds: ruleNames,
  }), [draftCameraIds, filterCameraLabel, ruleNames]);

  // FE-FIX09: в URL лежит точный ISO из drilldown Сводки; поле datetime-local
  // умеет только минуты, поэтому показываем округлённое значение, а в запрос
  // уходит то, что в URL, — пока пользователь сам не отредактирует поле.
  const displayFrom = isTimezoneAwareIso(draft.from) ? toLocalInputValue(draft.from) : draft.from;
  const displayTo = isTimezoneAwareIso(draft.to) ? toLocalInputValue(draft.to) : draft.to;

  const filtersAreEmpty = isEmptyEventFilters(appliedFilters);
  const draftIsEmpty = isEmptyEventFilters(draftFilters);

  const setDraftScope = useCallback((scope) => setDraft((prev) => ({ ...prev, scope })), []);
  const toggleDraft = (key, value) => setDraft((prev) => ({ ...prev, [key]: toggleValue(prev[key], value) }));

  const applyFilters = () => {
    // push, а не replace: применённые фильтры — самостоятельный шаг истории,
    // Back должен вернуть предыдущий набор фильтров, а не саму страницу.
    setSearchParams(writeEventFilters(searchParams, draftFilters), { replace: false });
  };

  const resetFilters = () => {
    const empty = createEmptyEventFilters();
    setDraft(toDraft(empty));
    // Сброс — replace: это не отдельный шаг истории, а отмена текущего фильтра.
    setSearchParams(writeEventFilters(searchParams, empty), { replace: true });
  };

  // FE-FIX04: карта строк по id события — цель для scrollIntoView при возврате.
  const rowRefs = useRef(new Map());
  const { rememberPosition } = useListScrollRestore({
    listReady: listState === 'ready',
    rowRefs,
  });

  useEffect(() => {
    const focus = focusRef.current;
    if (!focus || focusWaiting || listState !== 'ready') return;
    focusRef.current = null;
    const row = rowRefs.current.get(focus.id);
    if (row?.scrollIntoView) row.scrollIntoView({ block: 'center' });
  }, [focusWaiting, listState, items]);

  const pageIds = useMemo(() => items.map((event) => event.id), [items]);
  const pageTypes = useMemo(
    () => Object.fromEntries(items.map((event) => [event.id, eventTypeOf(event, source)])),
    [items, source]
  );
  const { catalogsByCamera } = useRuleCatalogs(items.map((event) => event.camera_id));

  const pageSelection = selection.pageState(pageIds);
  const checksLocked = selection.mode === 'filters' || bulkBusy;
  const checksTitle = selection.mode === 'filters' ? FILTER_SELECTION_HINT : undefined;
  const masterRef = useRef(null);
  useEffect(() => {
    if (masterRef.current) masterRef.current.indeterminate = pageSelection === 'some';
  }, [pageSelection, listState, items]);

  const selectAllByFilter = () => selection.selectByFilter(query, total);

  const applyExclusion = (excluded) => {
    if (bulkBusy || selection.mode !== 'ids' || selection.ids.size === 0) return;
    setBulkBusy(true);
    setBulkNotice('');
    setBulkError('');

    bulkSetEventAnalyticsExclusion([...selection.ids], excluded)
      .then((result) => {
        setBulkNotice(excluded
          ? `Исключено из аналитики ${result.changed}${result.alreadyInState ? ` · ${result.alreadyInState} уже были исключены` : ''}`
          : `Возвращено в аналитику ${result.changed}${result.alreadyInState ? ` · ${result.alreadyInState} уже учитывались` : ''}`);
        selection.clear();
        // Badge меняется только после authoritative refetch.
        return loadEvents();
      })
      .catch((error) => {
        // 5xx/сеть — выбор сохраняем, повтор разрешён; локально ничего не красим.
        setBulkError(describeWriteError(error, 'Не удалось изменить участие событий в аналитике'));
      })
      .finally(() => { setBulkBusy(false); });
  };

  const openEvent = (eventId, eventType = 'violation') => {
    rememberPosition(eventId);
    navigate(eventType === 'business' ? `/violations/${eventId}?type=business` : `/violations/${eventId}`);
  };

  const toggleDraftType = (typeId) => {
    const next = draftTypes.includes(typeId)
      ? draftTypes.filter((id) => id !== typeId)
      : [...draftTypes, typeId];
    setDraft((prev) => ({ ...prev, types: serializeEventTypes(next, allowedTypes) }));
  };

  const selectAllTypes = () => {
    const next = { ...appliedFilters, types: '' };
    setDraft((prev) => ({ ...prev, types: '' }));
    setSearchParams(writeEventFilters(searchParams, next), { replace: true });
  };

  // Колонка «Проверка» рисуется только если backend вообще присылает review:
  // пустой столбец с «—» ТЗ запрещает.
  const hasReview = items.some((event) => event.review);
  const columnKeys = [
    'select', 'photo', 'violation', 'camera', 'confirmed', 'status',
    ...(hasReview ? ['review'] : []), 'action',
  ];

  const renderBody = () => {
    if (listState === 'loading') return <ViolationStateBlock state="loading" />;

    if (listState === 'error') {
      return <ViolationStateBlock state="error" message={errorMessage} onRetry={loadEvents} />;
    }

    if (source === 'none') {
      return (
        <ViolationStateBlock state="empty" message="Не выбран ни один тип события">
          <button type="button" className="vio-btn" onClick={selectAllTypes}>Выбрать все типы</button>
        </ViolationStateBlock>
      );
    }

    if (items.length === 0) {
      return filtersAreEmpty ? (
        <ViolationStateBlock
          state="empty"
          message="Нарушений пока нет. Как только правило сработает, событие появится здесь."
        />
      ) : (
        <ViolationStateBlock
          state="empty"
          message="По выбранным фильтрам событий нет."
          onReset={resetFilters}
        />
      );
    }

    return (
      <>
        {tzScope && <p className="vio-field-hint"><TimezoneNotice scope={tzScope} /></p>}

        <EventBulkActionsBar
          total={total}
          selectedCount={selection.count}
          mode={selection.mode}
          onSelectAllByFilter={selectAllByFilter}
          onClear={selection.clear}
          onExclude={() => applyExclusion(true)}
          onRestore={() => applyExclusion(false)}
          busy={bulkBusy}
          notice={bulkNotice}
          error={bulkError}
        />

        {/* Нарушения и бизнес-события считаются по своим count-selected и складываются. */}
        <EventsSelectionStats selection={selection} baseParams={query} listEvents={listEventsFor} />
        <DuplicatesPanel
          selection={selection}
          ruleNames={ruleNames}
          listViolations={listEventsFor}
          onOpenPair={(candidate) => openEvent(candidate.reference_event_id)}
        />

        <div className="vio-table-wrapper">
          <table {...resize.tableProps(columnKeys, 'vio-table vio-events-table')}>
            <ResizableColgroup resize={resize} keys={columnKeys} />
            <thead>
              <tr>
                <ResizableTh resize={resize} colKey="select" resizable={false} className="vio-col-select">
                  <label className="vio-check-cell">
                    <input
                      ref={masterRef}
                      type="checkbox"
                      checked={pageSelection === 'all'}
                      disabled={checksLocked}
                      onChange={() => selection.togglePage(pageIds, pageSelection !== 'all', pageTypes)}
                      aria-label="Выбрать все события на странице"
                      title={checksTitle || 'Выбрать все события на странице'}
                    />
                    <span className="vio-check-cell-text">Выбрать все события на странице</span>
                  </label>
                </ResizableTh>
                <ResizableTh resize={resize} colKey="photo" className="vio-col-photo">Фото</ResizableTh>
                <ResizableTh resize={resize} colKey="violation">{violationOnly ? 'Нарушение' : 'Событие'}</ResizableTh>
                <ResizableTh resize={resize} colKey="camera">Камера</ResizableTh>
                <ResizableTh resize={resize} colKey="confirmed">Подтверждено</ResizableTh>
                <ResizableTh resize={resize} colKey="status">Статус</ResizableTh>
                {hasReview && <ResizableTh resize={resize} colKey="review" className="vio-col-review">Проверка</ResizableTh>}
                <ResizableTh resize={resize} colKey="action" className="vio-col-action">Открыть</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {items.map((event) => {
                const eventType = pageTypes[event.id];
                return (
                  <tr
                    key={event.id}
                    ref={(node) => {
                      if (node) rowRefs.current.set(event.id, node);
                      else rowRefs.current.delete(event.id);
                    }}
                    onClick={() => openEvent(event.id, eventType)}
                  >
                    <td className="vio-col-select" onClick={(clickEvent) => clickEvent.stopPropagation()}>
                      <label className="vio-check-cell">
                        <input
                          type="checkbox"
                          checked={selection.isSelected(event.id)}
                          disabled={checksLocked}
                          title={checksTitle}
                          onChange={() => selection.toggleOne(event.id, eventType)}
                          aria-label={`Выбрать событие ${event.id}`}
                        />
                      </label>
                    </td>
                    <td className="vio-col-photo" data-label="Фото">
                      <ViolationEvidenceThumbnail
                        eventId={event.id}
                        caption={`${cameraLabel(event.camera_id)} · ${formatInTimezone(event.confirmed_at, event.display_timezone || cameraTimezone(event.camera_id))}`}
                        onOpen={setLightbox}
                      />
                    </td>
                    <td data-label="Нарушение">
                      <span className="vio-cell-title">{event.rule_name || event.rule_id}</span>
                      <span className="vio-cell-sub">{formatEventCondition(event, { catalog: catalogsByCamera[event.camera_id] })}</span>
                      {!violationOnly && (
                        <span className={`vio-badge ${eventType === 'business' ? 'vio-badge--business' : 'vio-badge--category'}`}>
                          {EVENT_TYPE_LABELS[eventType]}
                        </span>
                      )}
                      {event.excluded_from_analytics && (
                        <span className="vio-badge vio-badge--deleted">Исключено из аналитики</span>
                      )}
                      {event.duplicate_state === 'confirmed_duplicate' && (
                        <span className="vio-badge vio-badge--duplicate">Дубль</span>
                      )}
                    </td>
                    <td data-label="Камера">{cameraLabel(event.camera_id)}</td>
                    <td data-label="Подтверждено">
                      {formatInTimezone(event.confirmed_at, event.display_timezone || cameraTimezone(event.camera_id))}
                    </td>
                    <td data-label="Статус"><ViolationStatusBadge status={event.status} /></td>
                    {hasReview && (
                      <td className="vio-col-review" data-label="Проверка">
                        {event.review ? getVerdictLabel(event.review.verdict) : '—'}
                      </td>
                    )}
                    <td className="vio-col-action">
                      <span className="vio-cell-actions">
                        <button
                          type="button"
                          className="vio-btn vio-btn--link"
                          onClick={(clickEvent) => { clickEvent.stopPropagation(); openEvent(event.id, eventType); }}
                        >
                          Открыть
                        </button>
                        <ChevronRight width={16} height={16} />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <NumberedPagination
          total={total}
          limit={limit}
          offset={offset}
          pageSizes={PAGE_SIZE_OPTIONS}
          onOffsetChange={(nextOffset) => setUrl({ offset: nextOffset }, { replace: false })}
          onLimitChange={(nextLimit) => setUrl({ limit: nextLimit, offset: 0 })}
        />
      </>
    );
  };

  const typeOptions = EVENT_TYPE_OPTIONS
    .filter((option) => allowedTypes.includes(option.id))
    .map((option) => ({ value: option.id, label: option.label }));

  return (
    <>
      <ScopeFilter
        value={draft.scope}
        onChange={setDraftScope}
        includeRules
        rulesKind={draftTypes.length === 1 ? draftTypes[0] : 'all'}
        knownNames={knownNames}
        onApply={applyFilters}
        applyDisabled={!dirty}
        onReset={resetFilters}
        resetDisabled={filtersAreEmpty && draftIsEmpty}
        idPrefix="events-scope"
        title="Фильтр событий"
      >
        <div className="vio-events-filter-extra">
          {typeOptions.length > 1 && (
            <CheckGroup
              legend="Тип события"
              options={typeOptions}
              checked={draftTypes}
              onToggle={toggleDraftType}
              noneIsEmpty
            />
          )}
          <CheckGroup
            legend="Участие в аналитике"
            options={ANALYTICS_GROUP}
            checked={draft.analytics}
            onToggle={(value) => toggleDraft('analytics', value)}
          />
          <CheckGroup
            legend="Дубли"
            options={DUPLICATE_GROUP}
            checked={draft.duplicate}
            onToggle={(value) => toggleDraft('duplicate', value)}
          />
          <CheckGroup
            legend="Статус"
            options={STATUS_GROUP}
            checked={draft.status}
            onToggle={(value) => toggleDraft('status', value)}
          />

          <div className="vio-events-period">
            <div className="vio-field">
              <label className="vio-field-label" htmlFor="vio-from-filter">С даты</label>
              <DateTimeInput
                id="vio-from-filter"
                value={displayFrom}
                onChange={(next) => setDraft((prev) => ({ ...prev, from: next }))}
                ariaLabel="С даты"
              />
            </div>
            <div className="vio-field">
              <label className="vio-field-label" htmlFor="vio-to-filter">По дату</label>
              <DateTimeInput
                id="vio-to-filter"
                value={displayTo}
                onChange={(next) => setDraft((prev) => ({ ...prev, to: next }))}
                ariaLabel="По дату"
              />
            </div>
            {singleCameraTimezone && (
              <p className="vio-field-hint">
                Период фильтра считается по часовому поясу {singleCameraTimezone}
              </p>
            )}
          </div>
        </div>
      </ScopeFilter>

      {renderBody()}

      {lightbox && (
        <EvidenceLightbox url={lightbox.url} caption={lightbox.caption} onClose={() => setLightbox(null)} />
      )}
    </>
  );
};

export default ViolationEventsTab;
